// File: frontend/src/components/entries/MatchInfoModal.tsx
import React, { useEffect } from "react";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";
import {
  FaTrophy,
  FaFlagCheckered,
  FaCalendarAlt,
  FaMapMarkerAlt,
  FaStopwatch,
  FaBullseye,
  FaFlag,
  FaVideo,
  FaClipboardList,
  FaUser,
} from "react-icons/fa";
import { GiWhistle } from "react-icons/gi";
import { MatchModel } from "../../models";
import { SportTypeEnum } from "../../models/enums/SportTypeEnum";
import { formatMatchDateLocal, formatMatchTimeLocal } from "../../utils/dateUtils";
import "./MatchInfoModal.css";

interface MatchInfoModalProps {
  match: MatchModel;
  onClose: () => void;
}

/**
 * Every secondary detail of a match — competition, round, date, time, venue,
 * extra-time/penalty result and the full referee crew — lives here
 * instead of in the entry-list header. The header only carries the identity of
 * the match (teams + score), so adding more detail later (line-ups, events)
 * grows this modal rather than pushing the entry list off screen.
 *
 * Rendered through a portal into document.body, same pattern as LockInfoModal
 * and ReportModal: an ancestor with overflow/transform would otherwise break
 * position: fixed.
 *
 * Each row is labelled with an icon rather than a text label. The translated
 * label is still attached as a tooltip (title) and as aria-label, so the
 * meaning stays reachable by hover and by screen readers while the grid gets
 * the horizontal space back for the values themselves.
 */

/**
 * Venue wording depends on the sport ("Stadyum" / "Salon" / "Arena").
 * Still used for the venue row's tooltip now that the visible label is an icon.
 */
const getVenueTranslationKey = (sportType: SportTypeEnum): string => {
  switch (sportType) {
    case SportTypeEnum.FOOTBALL:
      return "match_venue_football";
    case SportTypeEnum.BASKETBALL:
      return "match_venue_basketball";
    case SportTypeEnum.VOLLEYBALL:
      return "match_venue_volleyball";
    case SportTypeEnum.AMERICAN_FOOTBALL:
      return "match_venue_american_football";
    case SportTypeEnum.BASEBALL:
      return "match_venue_baseball";
    case SportTypeEnum.ICE_HOCKEY:
      return "match_venue_ice_hockey";
    case SportTypeEnum.HANDBALL:
      return "match_venue_handball";
    default:
      return "match_venue_football";
  }
};

/**
 * Referee display order — main official first, then assistants, video officials
 * and everything else. Kept identical to the previous header implementation so
 * the crew reads in the same order it always did, across all sports.
 */
const REFEREE_ROLE_ORDER: Record<string, number> = {
  // Football
  MAIN: 0, ASSISTANT_1: 1, ASSISTANT_2: 2,
  FOURTH_OFFICIAL: 3, VAR: 4, AVAR: 5,
  // Basketball
  CREW_CHIEF: 0, REFEREE: 1, UMPIRE: 2,
  // Volleyball
  FIRST_REFEREE: 0, SECOND_REFEREE: 1, SCORER: 2,
  ASSISTANT_SCORER: 3, LINE_JUDGE: 4,
  // Handball / Ice Hockey
  REFEREE_1: 0, REFEREE_2: 1,
  TABLE_OFFICIAL: 2, TIMEKEEPER: 3,
  LINESMAN_1: 2, LINESMAN_2: 3,
  // Rugby
  TMO: 3,
  // Baseball
  HOME_PLATE_UMPIRE: 0, BASE_UMPIRE_1: 1,
  BASE_UMPIRE_2: 2, BASE_UMPIRE_3: 3,
  // Always last
  OTHER: 99,
};

/**
 * Icon per referee role, grouped by what the official actually does rather than
 * by sport: whistle for officials who run the game, flag for line officials,
 * camera for video review, clipboard for table/administrative officials and a
 * stopwatch for timekeepers. Roles missing here fall back to a plain person.
 */
const REFEREE_ROLE_ICONS: Record<string, React.ReactNode> = {
  // On-field officials who carry the whistle
  MAIN: <GiWhistle />,
  REFEREE: <GiWhistle />,
  CREW_CHIEF: <GiWhistle />,
  UMPIRE: <GiWhistle />,
  FIRST_REFEREE: <GiWhistle />,
  SECOND_REFEREE: <GiWhistle />,
  REFEREE_1: <GiWhistle />,
  REFEREE_2: <GiWhistle />,
  HOME_PLATE_UMPIRE: <GiWhistle />,
  BASE_UMPIRE_1: <GiWhistle />,
  BASE_UMPIRE_2: <GiWhistle />,
  BASE_UMPIRE_3: <GiWhistle />,

  // Line officials
  ASSISTANT_1: <FaFlag />,
  ASSISTANT_2: <FaFlag />,
  LINESMAN_1: <FaFlag />,
  LINESMAN_2: <FaFlag />,
  LINE_JUDGE: <FaFlag />,

  // Video review
  VAR: <FaVideo />,
  AVAR: <FaVideo />,
  TMO: <FaVideo />,

  // Table / administrative officials
  FOURTH_OFFICIAL: <FaClipboardList />,
  SCORER: <FaClipboardList />,
  ASSISTANT_SCORER: <FaClipboardList />,
  TABLE_OFFICIAL: <FaClipboardList />,

  // Timing
  TIMEKEEPER: <FaStopwatch />,
};

export const MatchInfoModal: React.FC<MatchInfoModalProps> = ({ match, onClose }) => {
  const { t } = useTranslation();

  // Escape closes the modal — the header button is a small target and the
  // modal covers the entry list while open
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  const venueTerm = t(getVenueTranslationKey(match.sportType));

  const referees = match.referees
    ? [...match.referees].sort(
        (a, b) =>
          (REFEREE_ROLE_ORDER[a.refereeRole] ?? 9) - (REFEREE_ROLE_ORDER[b.refereeRole] ?? 9)
      )
    : [];

  // One row of the two-column detail grid. Rows without a value are skipped
  // entirely so an incomplete match never renders empty labels.
  // `label` is no longer painted — it becomes the icon's tooltip and its
  // accessible name instead.
  // `text` is the plain-string form of the value, used as the tooltip so a value
  // truncated with an ellipsis stays fully readable on hover.
  const renderRow = (
    label: string,
    icon: React.ReactNode,
    value: React.ReactNode,
    key: string,
    text?: string
  ) => (
    <div className="match-info__row" key={key}>
      <span className="match-info__icon" title={label} aria-label={label} role="img">
        {icon}
      </span>
      <span className="match-info__value" title={text ?? label}>
        {value}
      </span>
    </div>
  );

  // Related facts share one cell instead of taking a row each: date and kick-off
  // time read as a single moment, and extra time and penalties are two halves of
  // the same result. Grouping them keeps the grid to a few dense, scannable rows
  // rather than a tall ladder of near-empty ones.
  const matchRows: React.ReactNode[] = [];

  if (match.league) {
    matchRows.push(renderRow(t("match_info_league"), <FaTrophy />, match.league, "league", match.league));
  }
  if (match.round) {
    matchRows.push(renderRow(t("match_info_round"), <FaFlagCheckered />, match.round, "round", match.round));
  }

  // Date + kick-off time in one cell
  const dateText = formatMatchDateLocal(match.matchDate, match.matchTime);
  const timeText = formatMatchTimeLocal(match.matchDate, match.matchTime);
  matchRows.push(
    renderRow(
      `${t("match_detail_date")} · ${t("match_detail_time")}`,
      <FaCalendarAlt />,
      <>
        {dateText}
        <span className="match-info__sep">·</span>
        <span className="match-info__nowrap">{timeText}</span>
      </>,
      "datetime",
      `${dateText} · ${timeText}`
    )
  );

  if (match.stadiumName) {
    matchRows.push(renderRow(venueTerm, <FaMapMarkerAlt />, match.stadiumName, "venue", match.stadiumName));
  }

  // Extra time + penalties in one cell — each half is prefixed with its own
  // short label so a single score can never be mistaken for the other.
  const hasEt = match.homeScoreEt != null && match.awayScoreEt != null;
  const hasPen = match.homeScorePen != null && match.awayScorePen != null;
  if (hasEt || hasPen) {
    const etText = hasEt ? `${match.homeScoreEt} - ${match.awayScoreEt}` : "";
    const penText = hasPen ? `${match.homeScorePen} - ${match.awayScorePen}` : "";
    const parts: string[] = [];
    if (hasEt) parts.push(`${t("match_info_et")}: ${etText}`);
    if (hasPen) parts.push(`${t("match_info_pen")}: ${penText}`);
    matchRows.push(
      renderRow(
        parts.map((p) => p.split(":")[0]).join(" · "),
        hasEt ? <FaStopwatch /> : <FaBullseye />,
        <>
          {hasEt && (
            <span className="match-info__nowrap">
              <span className="match-info__tag">{t("match_info_et")}</span> {etText}
            </span>
          )}
          {hasEt && hasPen && <span className="match-info__sep">·</span>}
          {hasPen && (
            <span className="match-info__nowrap">
              <span className="match-info__tag">{t("match_info_pen")}</span> {penText}
            </span>
          )}
        </>,
        "result",
        parts.join(" · ")
      )
    );
  }

  return ReactDOM.createPortal(
    <div
      className="match-info-backdrop"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="match-info" role="dialog" aria-modal="true" aria-label={t("match_info_title")}>
        <div className="match-info__header">
          <span className="match-info__header-teams">
            {match.homeTeam} {match.homeScore} - {match.awayScore} {match.awayTeam}
          </span>
          <button type="button" className="match-info__close" onClick={onClose} aria-label={t("close")}>
            ×
          </button>
        </div>

        <div className="match-info__body">
          <div className="match-info__section-title">{t("match_info_section_match")}</div>
          <div className="match-info__grid">{matchRows}</div>

          {referees.length > 0 && (
            <>
              <div className="match-info__section-title">{t("match_info_section_referees")}</div>
              <div className="match-info__grid">
                {referees.map((ref) => {
                  const roleLabel = t(`match_referee_role_${ref.refereeRole.toLowerCase()}`);
                  return (
                    <div className="match-info__row" key={ref.assignmentId}>
                      <span
                        className="match-info__icon"
                        title={roleLabel}
                        aria-label={roleLabel}
                        role="img"
                      >
                        {REFEREE_ROLE_ICONS[ref.refereeRole] ?? <FaUser />}
                      </span>
                      <span className="match-info__value" title={ref.displayName}>
                        {ref.displayName}
                      </span>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};